import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { FocusTrapDirective } from '@cnt-workspace/ui';
import {
  BookingService, CancelRefundPreview, POLICY_LABEL, ToastService, cancelRefundPreview,
} from '@cnt-workspace/data-access';
import { IBooking } from '@cnt-workspace/models';
import { stringFromError } from '@curbnturf/form-helpers/src/lib/helpers/text';

/**
 * Cancel a booking (guest or host) or withdraw a pending request. The refund
 * preview mirrors the server's cancel rules (`cancelRefundPreview`), so what's
 * promised here is what gets issued.
 */
@Component({
  selector: 'cnt-booking-cancel-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, FocusTrapDirective],
  templateUrl: './booking-cancel-modal.component.html',
})
export class BookingCancelModalComponent implements OnInit {
  private bookingSvc = inject(BookingService);
  private toasts = inject(ToastService);

  @Input({ required: true }) booking!: IBooking;
  /** The listing's host is cancelling on the guest. */
  @Input() asHost = false;
  @Output() closed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<IBooking>();

  readonly POLICY_LABEL = POLICY_LABEL;
  reason = '';
  cancelling = false;
  preview!: CancelRefundPreview;

  ngOnInit(): void {
    this.preview = cancelRefundPreview(this.booking);
  }

  get isRequest(): boolean {
    return this.preview.kind === 'request';
  }

  close(): void {
    if (this.cancelling) return;
    this.closed.emit();
  }

  async confirm(): Promise<void> {
    this.cancelling = true;
    try {
      const updated = this.asHost
        ? await this.bookingSvc.hostCancel(this.booking.id, this.reason)
        : await this.bookingSvc.cancel(this.booking.id, this.reason);
      this.cancelling = false;
      if (!updated) {
        this.toasts.error('Could not cancel — please try again.');
        return;
      }
      // hostCancel toasts on its own.
      if (!this.asHost) this.toasts.info(this.successMessage());
      this.cancelled.emit(updated);
    } catch (err) {
      this.cancelling = false;
      this.toasts.error(err instanceof HttpErrorResponse
        ? stringFromError(err) || 'Could not cancel — please try again.'
        : 'Could not cancel — please try again.');
      console.error('[booking-cancel] cancel failed', err);
    }
  }

  private successMessage(): string {
    switch (this.preview.kind) {
      case 'request': return 'Request cancelled.';
      case 'none': return 'Booking cancelled.';
      default: return 'Booking cancelled. Your refund is on its way as CurbNTurf Cash.';
    }
  }
}
